import { ReactNode } from 'react';
import { tennisStats } from '@/mocks/estadisticas';

type Player = typeof tennisStats[0];

interface TennisTableProps {
  data: Player[];
  selectedFilter: string;
  selectedId?: number;
  onSelect?: (player: Player) => void;
}

interface ColDef {
  key: string;
  header: string;
  highlight: boolean;
  cell: (p: Player) => ReactNode;
}

function FormBadge({ r }: { r: string }) {
  return (
    <span className={`w-5 h-5 flex items-center justify-center rounded text-xs font-black ${r === 'W' ? 'bg-[#00e87a]/20 text-[#00e87a]' : 'bg-[#ef4444]/20 text-[#ef4444]'}`}>
      {r}
    </span>
  );
}

function Pill({ value, green, yellow }: { value: string | number; green: boolean; yellow?: boolean }) {
  const cls = green ? 'bg-[#00e87a]/15 text-[#00e87a]' : yellow ? 'bg-[#f59e0b]/15 text-[#f59e0b]' : 'bg-white/5 text-[#a8b2c1]';
  return <span className={`px-2 py-0.5 rounded text-xs font-bold ${cls}`}>{value}</span>;
}

const STAT_COLS: Record<string, ColDef[]> = {
  serve: [
    {
      key: 'fsw', header: '1st Srv Win%', highlight: true,
      cell: (p) => <span className={`font-bold text-sm ${p.firstServeWin > 75 ? 'text-[#00e87a]' : 'text-white'}`}>{p.firstServeWin}%</span>,
    },
    { key: 'aces', header: 'Aces/P', highlight: false, cell: (p) => <span className="text-[#a8b2c1] text-sm tabular-nums">{p.acesPerMatch}</span> },
    { key: 'df', header: 'DF/P', highlight: false, cell: (p) => <span className={`text-sm tabular-nums ${p.dfPerMatch > 2.5 ? 'text-[#ff6b35]' : 'text-[#a8b2c1]'}`}>{p.dfPerMatch}</span> },
  ],
  return: [
    {
      key: 'win', header: 'Win%', highlight: true,
      cell: (p) => <Pill value={`${p.winRate}%`} green={p.winRate >= 80} yellow={p.winRate >= 70 && p.winRate < 80} />,
    },
    { key: 'bpc', header: 'BP Conv%', highlight: false, cell: (p) => <Pill value={`${p.bpConverted}%`} green={p.bpConverted >= 50} yellow={p.bpConverted >= 43 && p.bpConverted < 50} /> },
    { key: 'bps', header: 'BP Saved%', highlight: false, cell: (p) => <span className="text-[#a8b2c1] text-sm tabular-nums">{p.bpSaved}%</span> },
  ],
  breakpoints: [
    {
      key: 'bps', header: 'BP Saved%', highlight: true,
      cell: (p) => <span className={`font-bold text-sm ${p.bpSaved >= 70 ? 'text-[#00e87a]' : p.bpSaved >= 60 ? 'text-[#f59e0b]' : 'text-white'}`}>{p.bpSaved}%</span>,
    },
    { key: 'bpc', header: 'BP Conv%', highlight: false, cell: (p) => <Pill value={`${p.bpConverted}%`} green={p.bpConverted >= 50} yellow={p.bpConverted >= 43 && p.bpConverted < 50} /> },
    { key: 'win', header: 'Win%', highlight: false, cell: (p) => <span className="text-[#a8b2c1] text-sm tabular-nums">{p.winRate}%</span> },
  ],
  aces: [
    {
      key: 'aces', header: 'Aces/P', highlight: true,
      cell: (p) => <span className={`font-bold text-sm ${p.acesPerMatch > 8 ? 'text-[#00e87a]' : 'text-white'}`}>{p.acesPerMatch}</span>,
    },
    { key: 'df', header: 'DF/P', highlight: false, cell: (p) => <span className={`text-sm tabular-nums ${p.dfPerMatch > 2.5 ? 'text-[#ff6b35]' : 'text-[#a8b2c1]'}`}>{p.dfPerMatch}</span> },
    { key: 'fsw', header: '1st Srv Win%', highlight: false, cell: (p) => <span className="text-[#a8b2c1] text-sm tabular-nums">{p.firstServeWin}%</span> },
    { key: 'win', header: 'Win%', highlight: false, cell: (p) => <span className="text-[#a8b2c1] text-sm tabular-nums">{p.winRate}%</span> },
  ],
  sets: [
    {
      key: 'sets', header: 'Sets Ganados%', highlight: true,
      cell: (p) => <span className={`font-bold text-sm ${p.setsWon > 88 ? 'text-[#00e87a]' : 'text-white'}`}>{p.setsWon}%</span>,
    },
    { key: 'win', header: 'Win%', highlight: false, cell: (p) => <Pill value={`${p.winRate}%`} green={p.winRate >= 80} yellow={p.winRate >= 70 && p.winRate < 80} /> },
    { key: 'bps', header: 'BP Saved%', highlight: false, cell: (p) => <span className="text-[#a8b2c1] text-sm tabular-nums">{p.bpSaved}%</span> },
  ],
};

const SORT_FN: Record<string, (a: Player, b: Player) => number> = {
  serve: (a, b) => b.firstServeWin - a.firstServeWin,
  return: (a, b) => b.winRate - a.winRate,
  breakpoints: (a, b) => b.bpSaved - a.bpSaved,
  aces: (a, b) => b.acesPerMatch - a.acesPerMatch,
  sets: (a, b) => b.setsWon - a.setsWon,
};

export default function TennisTable({ data, selectedFilter, selectedId, onSelect }: TennisTableProps) {
  const cols = STAT_COLS[selectedFilter] ?? STAT_COLS.serve;
  const sortFn = SORT_FN[selectedFilter] ?? SORT_FN.serve;
  const sorted = [...data].sort(sortFn);

  return (
    <div className="bg-[#151719] border border-[#2a2d3a] rounded-xl overflow-hidden">
      <div className="px-5 py-4 border-b border-[#2a2d3a] flex items-center justify-between">
        <h3 className="text-white font-bold text-sm">
          Estadísticas de Jugadores — Tenis
          <span className="ml-2 px-2 py-0.5 rounded-md bg-[#00e87a]/10 text-[#00e87a] text-xs font-bold">
            {cols.find((c) => c.highlight)?.header}
          </span>
        </h3>
        <span className="text-[#6b7280] text-xs">{sorted.length} jugadores</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="border-b border-[#2a2d3a]">
              <th className="px-5 py-3 text-left text-[#6b7280] text-xs font-semibold uppercase tracking-wide">Jugador</th>
              <th className="px-4 py-3 text-center text-[#6b7280] text-xs font-semibold uppercase">Rank</th>
              <th className="px-4 py-3 text-center text-[#6b7280] text-xs font-semibold uppercase">PJ</th>
              {cols.map((c) => (
                <th
                  key={c.key}
                  className={`px-4 py-3 text-center text-xs font-semibold uppercase ${c.highlight ? 'text-[#00e87a]' : 'text-[#6b7280]'}`}
                >
                  {c.header}
                </th>
              ))}
              <th className="px-4 py-3 text-center text-[#6b7280] text-xs font-semibold uppercase">Forma</th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((p, i) => (
              <tr
                key={p.id}
                onClick={() => onSelect?.(p)}
                className={`border-b border-[#1e2028] transition-colors cursor-pointer ${
                  selectedId === p.id
                    ? 'bg-[#00e87a]/8 border-l-2 border-l-[#00e87a]'
                    : i === 0
                    ? 'bg-[#00e87a]/3 hover:bg-[#00e87a]/6'
                    : 'hover:bg-white/3'
                }`}
              >
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <span className="text-[#3d4149] text-sm font-medium w-5 text-right tabular-nums">{i + 1}</span>
                    <div>
                      <p translate="no" className="text-white text-sm font-semibold">{p.player}</p>
                      <p translate="no" className="text-[#6b7280] text-xs">{p.tour}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-4 text-center">
                  <span className="text-[#ffd700] font-bold text-sm">#{p.ranking}</span>
                </td>
                <td className="px-4 py-4 text-center text-[#a8b2c1] text-sm">{p.played}</td>
                {cols.map((c) => (
                  <td key={c.key} className={`px-4 py-4 text-center ${c.highlight ? 'bg-[#00e87a]/3' : ''}`}>
                    {c.cell(p)}
                  </td>
                ))}
                <td className="px-4 py-4">
                  <div className="flex items-center justify-center gap-0.5">
                    {p.form.map((r, j) => <FormBadge key={j} r={r} />)}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
